import React, {useEffect, useMemo, useRef, useState} from 'react';
import {wdiCore, WdiModalAppProps} from '@/components/WdiControls';
import {FieldProps} from '@/components/WdiControls';
import type {ProFormInstance} from '@ant-design/pro-form';
import {EC} from '@/services/mdm/ec';
import {WdiSelect, WdiSelectFormat, WdiTreeSelect} from '@/components/WdiControls/WdiSelect';
import {Form, Input, message, Tooltip} from 'antd';
import {WdiModalForm} from '@/components/WdiControls/WdiModalForm';
import {ResponseStatus} from '@/utils/request';
import {Enum} from '@/enums/system';
import {SalesContractTerms} from '@/services/contract/salescontractterms';
import {DataOptions} from '@/services/basic/metadata';
import {Product} from '@/services/mdm/product';
import {RevenueType} from '@/services/mdm/revenuetype';
import {convertToResult, initSalesContractRateGrid, RevenueGroupType, SCTRateGrid,} from './rate-grid';
import {ModalAppTypeEnums} from './details';
import {isDupSalesContractTerms} from '@/pages/contract/SalesContractApply/components/data';
import {WdiInputNumber} from '@/components/WdiControls/WdiInputNumber';

const {TextArea} = Input;

const ecDataLoadingFactory = (pageTypeId: number) => {
  return async (params: any) => {
    const response = await EC.searchEc3(
      Object.assign(
        {
          isActive: true,
          pageTypeIds: [pageTypeId].join(),
        },
        params,
      ),
    );
    const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
      textField: 'ecName',
      valueField: 'ecId',
      id: 'ecId',
      pId: 'parentId',
      level: 'ecLevel',
    }, response.data, (node: any, item: any) => {
      node.title = (
        <Tooltip placement="topLeft" title={item.remark}><span>{node.title}</span></Tooltip>
      );
      return node;
    });
    return {treeData, dict}
  };
};

const processModeDataLoading = async (params: any) => {
  const response = await DataOptions.search(Object.assign({ typeGroup: 13 }, params));
  return response.data.map((item) => ({ value: item.value, label: item.name }));
};

const productTreeDataLoading = async (params: any) => {
  const response = await Product.searchAll(
    Object.assign(
      {
        productLevels: [
          Enum.ProductLevel.ROOT,
          Enum.ProductLevel.PRODUCT1,
          Enum.ProductLevel.PRODUCT2,
          Enum.ProductLevel.PRODUCT3,
          Enum.ProductLevel.PRODUCT4,
          Enum.ProductLevel.PRODUCT5,
        ].join(),
        isActive: true,
      },
      params,
    ),
  );
  const { treeData, dict } = FieldProps.TreeSelect.formatTreeData(
    {
      textField: 'codeOrName',
      valueField: 'productId',
      id: 'productId',
      pId: 'parentId',
      level: 'productLevel',
    },
    response.data,
  );
  return { treeData, dict };
};
const exProductTreeDataLoading = async (params: any) => {
  const response = await Product.searchAll(
    Object.assign(
      {
        productLevels: [
          Enum.ProductLevel.PRODUCT1,
          Enum.ProductLevel.PRODUCT2,
          Enum.ProductLevel.PRODUCT3,
          Enum.ProductLevel.PRODUCT4,
          Enum.ProductLevel.PRODUCT5,
        ].join(),
        isActive: true,
      },
      params,
    ),
  );
  const { treeData, dict } = FieldProps.TreeSelect.formatTreeData(
    {
      textField: 'codeOrName',
      valueField: 'productId',
      id: 'productId',
      pId: 'parentId',
      level: 'productLevel',
    },
    response.data,
  );
  return { treeData, dict };
};

const revenueTypeDataLoadingFactory = (pageTypeId: number) => {
  return async (params: any) => {
    const response = await RevenueType.search(
      Object.assign(
        {
          applyType: Enum.RevApplyType.CONTRACT,
        },
        params,
      ),
    );
    return response.data.map((item) => ({
      value: item.revenueTypeId,
      label: item.revenueTypeName,
    }));
  };
};

const settlementFrequencyDataLoading = async (params: any) => {
  const response = await DataOptions.search(
    Object.assign({ typeGroup: 15, notInValues: '100' }, params),
  );
  return response.data.map((item) => ({ value: item.value, label: item.name }));
};

const paymentMethodDataLoading = async (params: any) => {
  const response = await DataOptions.search(
    Object.assign({ typeGroup: 5, inValues: '1,2,3,5,7' }, params),
  );
  return response.data.map((item) => ({ value: item.value, label: item.name }));
};

const SalesContractTermsAutoEditApp: React.FC<WdiModalAppProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [editMode, setEditMode] = useState<boolean>();
  const [revenueGroupType, setRevenueGroupType] = useState<number>(RevenueGroupType.None);
  const [rateOriginalHidden, setRateOriginalHidden] = useState(true);
  const [calcMonthlyTypeHidden, setCalcMonthlyTypeHidden] = useState(true);
  const [expandKeysProductRate, setExpandKeysProductRate] = useState<string[]>();
  const [expandKeysProductRateEx, setExpandKeysProductRateEx] = useState<string[]>();
  const [expandKeysProductReachEx, setExpandKeysProductReachEx] = useState<string[]>();
  const [expandKeysProductReach, setExpandKeysProductReach] = useState<string[]>();

  const [nValueHidden, setNValueHidden] = useState(true);

  const ecDataLoading = useMemo(() => {
    return ecDataLoadingFactory(props.data?.pageTypeId);
  }, [props.data?.pageTypeId]);

  const revenueTypeDataLoading = useMemo(() => {
    return revenueTypeDataLoadingFactory(props.data?.pageTypeId);
  }, [props.data?.pageTypeId]);

  const onRevenueTypeChange = (revenueTypeId: number) => {
    setRevenueGroupType(RevenueGroupType.convert(revenueTypeId));
  };

  useEffect(() => {
    formRef.current?.setFieldsValue({
      processMode: props?.data?.row?.processMode,
    });
    if (props.data.row) {
      const type = Number(props?.type);
      setEditMode(type == ModalAppTypeEnums.EditAuto);

      const detail = props.data.row;
      formRef.current?.setFieldsValue({
        salesContractTermsId: detail.salesContractTermsId,
        salesContractId: detail.salesContractId,
        ecId: detail.ecId,
        revenueType: detail.revenueType,
        settlementFrequency: detail.settlementFrequency,
        paymentMethodId: detail.paymentMethodId,
        remark: detail.remark,
      });

      if (detail.calcMonthlyType) {
        setCalcMonthlyTypeHidden(false);
        formRef.current?.setFieldsValue({
          calcMonthlyType: detail.calcMonthlyType,
        });
      }
      if (detail.nValue) {
        setNValueHidden(false);
        formRef.current?.setFieldsValue({
          nValue: detail.nValue,
        });
      }
      if (!wdiCore.Object.IsNullOrUndefined(detail.rateOriginal)) {
        setRateOriginalHidden(false);
        formRef.current?.setFieldsValue({
          rateOriginal: detail.rateOriginal,
        });
      }

      if (detail.revenueType) {
        onRevenueTypeChange(detail.revenueType);
      }

      if (detail.productIdsReach) {
        Product.searchAll({ productIds: detail.productIdsReach }).then((response) => {
          if (response.data && response.data.length > 0) {
            setExpandKeysProductReach(response.data.map((item) => `${item.productId}`));
            formRef.current?.setFieldsValue({
              productIdsReach: response.data.map((product) => ({
                value: product.productId,
                label: product.productName,
              })),
            });
          }
        });
      }

      if (detail.productIdsRebate) {
        Product.searchAll({ productIds: detail.productIdsRebate }).then((response) => {
          if (response.data && response.data.length > 0) {
            setExpandKeysProductRate(response.data.map((item) => `${item.productId}`));
            formRef.current?.setFieldsValue({
              productIdsRebate: response.data.map((product) => ({
                value: product.productId,
                label: product.productName,
              })),
            });
          }
        });
      }

      if (detail.productIdsReachEx) {
        Product.searchAll({ productIds: detail.productIdsReachEx }).then((response) => {
          if (response.data && response.data.length > 0) {
            setExpandKeysProductReachEx(response.data.map((item) => `${item.productId}`));
            formRef.current?.setFieldsValue({
              productIdsReachEx: response.data.map((product) => ({
                value: product.productId,
                label: product.productName,
              })),
            });
          }
        });
      }

      if (detail.productIdsRebateEx) {
        Product.searchAll({ productIds: detail.productIdsRebateEx }).then((response) => {
          if (response.data && response.data.length > 0) {
            setExpandKeysProductRateEx(response.data.map((item) => `${item.productId}`));
            formRef.current?.setFieldsValue({
              productIdsRebateEx: response.data.map((product) => ({
                value: product.productId,
                label: product.productName,
              })),
            });
          }
        });
      }
    }

    formRef.current?.setFieldsValue({
      termsContentRate: initSalesContractRateGrid(
        props?.data?.row?.termsContent,
        props?.data?.row?.settlementRateType,
        RevenueGroupType.convert(props?.data?.row?.revenueType),
      ),
    });
  }, [props.data.row]);

  return (
    <WdiModalForm
      formRef={formRef}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 17 }}
      labelAlign="left"
      layout="horizontal"
      title={props.title}
      visible={props.visible}
      preserve={true}
      modalProps={{
        onCancel: () => {
          props.onClose ? props.onClose() : null;
        },
        destroyOnClose: true,
        maskClosable: false,
      }}
      onFinish={async (values) => {
        const {
          termsContentRate,
          productIdsReach,
          productIdsRebate,
          productIdsReachEx,
          productIdsRebateEx,
          ...entity
        } = values;

        const gridResponse: any = convertToResult(termsContentRate, entity.revenueType);
        if (gridResponse.code != ResponseStatus.SUCCESS) {
          message.error(String(gridResponse?.message));
          return;
        }
        entity.settlementRateType = gridResponse.data.rateType;
        entity.termsContent = gridResponse.data.termsContent;

        if (productIdsReach) {
          entity.productIdsReach = productIdsReach.map((item: any) => item.value).join();
        }

        if (productIdsRebate) {
          entity.productIdsRebate = productIdsRebate.map((item: any) => item.value).join();
        }

        if (productIdsReachEx) {
          entity.productIdsReachEx = productIdsReachEx.map((item: any) => item.value).join();
        }

        if (productIdsRebateEx) {
          entity.productIdsRebateEx = productIdsRebateEx.map((item: any) => item.value).join();
        }

        const salesContract = {
          pageTypeId: props.data?.pageTypeId,
          soldToCode: props.data?.soldToCode,
          soldToGroupId: props.data?.soldToGroupId,
          custSysCode: props.data?.custSysCode,
        };

        const response = await SalesContractTerms.validateDetail({
          salesContractTerms: entity,
          salesContract: salesContract,
        });
        if (response.code !== ResponseStatus.SUCCESS) {
          message.error(response.message);
        } else {
          if (isDupSalesContractTerms(props.data.rows, response.data, props.data.rowIndex)) {
            message.error('合同条款已存在');
          } else {
            if (props.onSave) {
              props.onSave({
                code: ResponseStatus.SUCCESS,
                message: '',
                data: response.data,
              });
            }
          }
        }
      }}
    >
      <Form.Item name="salesContractTermsId" label="salesContractTermsId" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="salesContractId" label="salesContractId" hidden>
        <Input />
      </Form.Item>
      <Form.Item
        required
        name="ecId"
        label="费用项"
        rules={[{ required: true, message: '费用项不能为空!' }]}
      >
        <WdiTreeSelect
          {...{
            placeholder: '请选择费用项',
            dataLoading: ecDataLoading,
          }}
        />
      </Form.Item>
      <Form.Item
        name="processMode"
        label="处理方式"
        rules={[{ required: true, message: '处理方式不能为空!' }]}
      >
        <WdiSelect
          {...{
            placeholder: '请选择处理方式',
            disabled: true,
            dataLoading: processModeDataLoading,
          }}
        />
      </Form.Item>
      <Form.Item
        name="productIdsReach"
        label="达量产品"
        rules={[{ required: true, message: '达量产品不能为空!' }]}
      >
        <WdiTreeSelect
          {...{
            multiple: true,
            placeholder: '请选择达量产品',
            defaultExpandKeys: expandKeysProductReach,
            isExpanded: true,
            dataLoading: productTreeDataLoading,
            onExSelect: (value: any, node: any, dict: any) => {
              const values = formRef.current?.getFieldValue('productIdsReach');
              const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, { value });
              formRef.current?.setFieldsValue({
                productIdsReach: rtv,
              });
            },
          }}
        />
      </Form.Item>
      <Form.Item
        name="productIdsRebate"
        label="返利产品"
        rules={[{ required: true, message: '返利产品不能为空!' }]}
      >
        <WdiTreeSelect
          {...{
            multiple: true,
            isExpanded: true,
            placeholder: '请选择返利产品',
            defaultExpandKeys: expandKeysProductRate,
            dataLoading: exProductTreeDataLoading,
            onExSelect: (value: any, node: any, dict: any) => {
              const values = formRef.current?.getFieldValue('productIdsRebate');
              const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, { value });
              formRef.current?.setFieldsValue({
                productIdsRebate: rtv,
              });
            },
          }}
        />
      </Form.Item>
      <Form.Item
        required
        name="revenueType"
        label="核算类型"
        rules={[{ required: true, message: '核算类型不能为空!' }]}
      >
        <WdiSelect
          {...{
            placeholder: '请选择核算类型',
            dataLoading: revenueTypeDataLoading,
            onChange: onRevenueTypeChange,
          }}
        />
      </Form.Item>
      <Form.Item
        required
        name="settlementFrequency"
        label="结案频率"
        rules={[{ required: true, message: '结案频率不能为空!' }]}
      >
        <WdiSelect
          {...{
            placeholder: '请选择结案频率',
            disabled: editMode,
            dataLoading: settlementFrequencyDataLoading,
            onChange: (param: any) => {
              if (WdiSelectFormat.getvalue(param) > 1) {
                setRateOriginalHidden(false);
              } else {
                setRateOriginalHidden(true);
                formRef.current?.setFieldsValue({
                  rateOriginal: undefined,
                });
              }
              if (WdiSelectFormat.getvalue(param) == 1) {
                setCalcMonthlyTypeHidden(false);
                formRef.current?.setFieldsValue({
                  calcMonthlyType: 1,
                });
              } else {
                setCalcMonthlyTypeHidden(true);
                setNValueHidden(true);
                formRef.current?.setFieldsValue({
                  calcMonthlyType: undefined,
                  nValue: undefined,
                });
              }
            },
          }}
        />
      </Form.Item>
      <Form.Item
        hidden={calcMonthlyTypeHidden}
        name="calcMonthlyType"
        label="月度计算方式"
        rules={[{ required: !calcMonthlyTypeHidden }]}
      >
        <WdiSelect
          defaultValue={1}
          {...{
            placeholder: '请选择月度计算方式',
            options: [
              { label: '整月计算', value: 1 },
              { label: '每月前N天', value: 2 },
            ],
            onChange: (param: any) => {
              if (WdiSelectFormat.getvalue(param) == 2) {
                setNValueHidden(false);
              } else {
                setNValueHidden(true);
                formRef.current?.setFieldsValue({
                  nValue: undefined,
                });
              }
            },
          }}
        />
      </Form.Item>
      <Form.Item
        hidden={nValueHidden}
        name="nValue"
        label="N的数值"
        rules={[{ required: !nValueHidden }]}
      >
        <WdiInputNumber format={'d'} digits={0} min={1} max={31} />
      </Form.Item>
      <Form.Item
        required
        name="paymentMethodId"
        label="支付方式"
        rules={[{ required: true, message: '支付方式不能为空!' }]}
      >
        <WdiSelect
          {...{
            placeholder: '请选择支付方式',
            dataLoading: paymentMethodDataLoading,
          }}
        />
      </Form.Item>
      <Form.Item
        hidden={rateOriginalHidden}
        name="rateOriginal"
        label="初始比例"
        rules={[{ required: !rateOriginalHidden }]}
      >
        <WdiInputNumber format={'p'} digits={4} min={0} />
      </Form.Item>
      <Form.Item
        required
        name="termsContentRate"
        label="费用标准"
        rules={[{ required: true, message: '费用标准不能为空!' }]}
      >
        <SCTRateGrid revenueGroupType={revenueGroupType} />
      </Form.Item>
      <Form.Item name="productIdsReachEx" label="排除达量产品">
        <WdiTreeSelect
          {...{
            multiple: true,
            placeholder: '请选择排除达量产品',
            defaultExpandKeys: expandKeysProductReachEx,
            dataLoading: exProductTreeDataLoading,
            onExSelect: (value: any, node: any, dict: any) => {
              const values = formRef.current?.getFieldValue('productIdsReachEx');
              const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, { value });
              formRef.current?.setFieldsValue({
                productIdsReachEx: rtv,
              });
            },
          }}
        />
      </Form.Item>
      <Form.Item name="productIdsRebateEx" label="排除返利产品">
        <WdiTreeSelect
          {...{
            multiple: true,
            placeholder: '请选择排除返利产品',
            defaultExpandKeys: expandKeysProductRateEx,
            dataLoading: exProductTreeDataLoading,
            onExSelect: (value: any, node: any, dict: any) => {
              const values = formRef.current?.getFieldValue('productIdsRebateEx');
              const rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, { value });
              formRef.current?.setFieldsValue({
                productIdsRebateEx: rtv,
              });
            },
          }}
        />
      </Form.Item>
      <Form.Item name="remark" label="备注">
        <TextArea rows={3} style={{ width: '100%' }} />
      </Form.Item>
    </WdiModalForm>
  );
};
export default SalesContractTermsAutoEditApp;
